/**
 * @Module: PantryCheck
 * @Status: Ready
 * @Summary: Independent have/need toggles per ingredient — not mutually exclusive
 * @Description: Each ingredient has two independent toggles: check (pantry) and
 *   cart (shopping list). Both can be active simultaneously. Pre-marks pantry items.
 */

import React, { useState, useMemo } from 'react';
import {
  Box, VStack, HStack, Text, IconButton, Button, Badge, useToast, Divider,
} from '@chakra-ui/react';
import type { Recipe } from '../../types/recipe';
import { useKitchen } from '../../contexts/KitchenContext';
import { useShoppingList } from '../../contexts/ShoppingListContext';
import { ingredientInList, normalizeIngredient } from '../../utils/ingredientMatcher';
import { formatAmountWithUnit } from '../../utils/formatAmount';
import { categorize, SECTIONS } from '../../utils/ingredientCategories';
import { AppIcon } from '../icons/AppIcon';

interface IngredientRow {
  name: string;
  amount: number;
  unit: string;
  section: string;
  inPantry: boolean;
}

interface ItemFlags {
  have: boolean;
  need: boolean;
}

interface PantryCheckProps {
  recipe: Recipe;
  onComplete?: () => void;
}

const PantryCheck: React.FC<PantryCheckProps> = ({ recipe, onComplete }) => {
  const { profile, updateProfile } = useKitchen();
  const { addItems } = useShoppingList();
  const toast = useToast();
  const pantryStaples = profile?.pantryStaples || [];

  const ingredients = useMemo(() => {
    const seen = new Map<string, IngredientRow>();
    for (const step of recipe.steps) {
      for (const ing of step.ingredients) {
        const key = normalizeIngredient(ing.name);
        if (!seen.has(key)) {
          seen.set(key, {
            name: ing.name,
            amount: ing.amount,
            unit: ing.unit,
            section: categorize(ing.name),
            inPantry: ingredientInList(ing.name, pantryStaples),
          });
        }
      }
    }
    const result: IngredientRow[] = [];
    seen.forEach(v => result.push(v));
    return result;
  }, [recipe, pantryStaples]);

  // Independent flags — both can be true
  const [flags, setFlags] = useState<Map<string, ItemFlags>>(() => {
    const m = new Map<string, ItemFlags>();
    ingredients.forEach(i => {
      m.set(normalizeIngredient(i.name), { have: i.inPantry, need: false });
    });
    return m;
  });

  const toggleHave = (name: string) => {
    setFlags(prev => {
      const next = new Map(prev);
      const key = normalizeIngredient(name);
      const current = next.get(key) || { have: false, need: false };
      next.set(key, { ...current, have: !current.have });
      return next;
    });
  };

  const toggleNeed = (name: string) => {
    setFlags(prev => {
      const next = new Map(prev);
      const key = normalizeIngredient(name);
      const current = next.get(key) || { have: false, need: false };
      next.set(key, { ...current, need: !current.need });
      return next;
    });
  };

  const haveCount = ingredients.filter(i => flags.get(normalizeIngredient(i.name))?.have).length;
  const needCount = ingredients.filter(i => flags.get(normalizeIngredient(i.name))?.need).length;

  const grouped = useMemo(() => {
    const groups = new Map<string, IngredientRow[]>();
    ingredients.forEach(i => {
      const list = groups.get(i.section) || [];
      list.push(i);
      groups.set(i.section, list);
    });
    return groups;
  }, [ingredients]);

  const activeSections = SECTIONS.filter(s => grouped.has(s.key));

  const handleSave = () => {
    // Add newly-marked "have" items to pantry
    const newPantryItems = ingredients
      .filter(i => {
        const f = flags.get(normalizeIngredient(i.name));
        return f?.have && !i.inPantry;
      })
      .map(i => i.name);

    if (newPantryItems.length > 0 && profile) {
      const existingNormalized = new Set(pantryStaples.map(s => normalizeIngredient(s)));
      const toAdd = newPantryItems.filter(n => !existingNormalized.has(normalizeIngredient(n)));
      if (toAdd.length > 0) {
        updateProfile({ ...profile, pantryStaples: [...pantryStaples, ...toAdd] });
      }
    }

    // Add "need" items to shopping list
    const needItems = ingredients.filter(i => flags.get(normalizeIngredient(i.name))?.need);
    if (needItems.length > 0) {
      addItems(needItems.map(i => ({
        name: i.name,
        amount: i.amount,
        unit: i.unit,
        recipeTitle: recipe.title,
      })));
    }

    const msg = [];
    if (newPantryItems.length > 0) msg.push(`${newPantryItems.length} to pantry`);
    if (needItems.length > 0) msg.push(`${needItems.length} to shopping list`);

    toast({
      title: msg.length > 0 ? msg.join(' · ') : 'Done',
      status: 'success', duration: 3000, isClosable: true, position: 'top',
    });

    onComplete?.();
  };

  return (
    <Box w="100%">
      <HStack justify="space-between" mb={3}>
        <Text fontSize="sm" fontWeight={600} fontFamily="heading" color="text.primary">
          {recipe.title}
        </Text>
        <HStack spacing={2}>
          <Badge colorScheme="sage" variant="subtle" fontSize="2xs" borderRadius="full" px={2}>
            {haveCount} have
          </Badge>
          {needCount > 0 && (
            <Badge colorScheme="terracotta" variant="subtle" fontSize="2xs" borderRadius="full" px={2}>
              {needCount} need
            </Badge>
          )}
        </HStack>
      </HStack>

      <VStack spacing={0} align="stretch" bg="surface.card" borderRadius="lg"
        borderWidth="1px" borderColor="border.subtle" overflow="hidden">
        {activeSections.map((section, si) => {
          const sectionItems = grouped.get(section.key) || [];
          return (
            <Box key={section.key}>
              <HStack px={4} py={2} bg="surface.header" spacing={2}>
                <AppIcon name={section.icon} size={14} color={section.color} />
                <Text fontSize="2xs" fontWeight={700} textTransform="uppercase"
                  letterSpacing="wider" color={section.color}>
                  {section.label}
                </Text>
              </HStack>
              <VStack spacing={0} align="stretch">
                {sectionItems.map((item) => {
                  const key = normalizeIngredient(item.name);
                  const f = flags.get(key) || { have: false, need: false };

                  return (
                    <HStack
                      key={item.name}
                      px={3}
                      py={2}
                      justify="space-between"
                      bg={f.have && f.need ? 'rgba(91,138,91,0.08)' : f.have ? 'surface.active' : f.need ? 'rgba(240,128,64,0.06)' : 'transparent'}
                      transition="background 150ms"
                    >
                      <Text
                        fontSize="sm"
                        color={f.have ? 'text.primary' : f.need ? 'terracotta.500' : 'text.secondary'}
                        fontWeight={f.have || f.need ? 500 : 400}
                        flex={1}
                        noOfLines={1}
                      >
                        {formatAmountWithUnit(item.amount, item.unit)} {item.name}
                      </Text>

                      <HStack spacing={1}>
                        <IconButton
                          aria-label="I have this"
                          icon={<AppIcon name="ui.check" size={14} />}
                          size="xs"
                          variant="ghost"
                          borderRadius="md"
                          bg={f.have ? 'sage.500' : 'transparent'}
                          color={f.have ? 'white' : 'text.muted'}
                          _hover={{ bg: f.have ? 'sage.600' : 'surface.hover' }}
                          onClick={() => toggleHave(item.name)}
                        />
                        <IconButton
                          aria-label="Add to shopping list"
                          icon={<AppIcon name="ui.shoppingCart" size={14} />}
                          size="xs"
                          variant="ghost"
                          borderRadius="md"
                          bg={f.need ? 'terracotta.400' : 'transparent'}
                          color={f.need ? 'white' : 'text.muted'}
                          _hover={{ bg: f.need ? 'terracotta.500' : 'surface.hover' }}
                          onClick={() => toggleNeed(item.name)}
                        />
                      </HStack>
                    </HStack>
                  );
                })}
              </VStack>
              {si < activeSections.length - 1 && <Divider borderColor="border.subtle" />}
            </Box>
          );
        })}
      </VStack>

      <Button
        variant="kitchen-primary"
        w="100%"
        borderRadius="lg"
        mt={4}
        onClick={handleSave}
        leftIcon={<AppIcon name="ui.check" size={16} />}
      >
        Save
      </Button>
    </Box>
  );
};

export default PantryCheck;
